import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AiNarrativeBlock from './AiNarrativeBlock';
import {
  analyzeStudentNarrative,
  suggestInterventionsNarrative,
  draftParentEmail,
} from '../../../lib/insights/narrative';
import { FUNCTIONS_OF_BEHAVIOR } from '../../../lib/pbisConstants';

// ---------------------------------------------------------------------------
// StudentInsightPanel — the deep-dive view for one selected student.
//
// This is where the deterministic analytics from analyzeCaseload() get
// laid out side-by-side with the on-demand AI narrative controls. The
// numbers are always visible; the AI text renders only after the teacher
// asks for it (one Supabase Edge Function call per button).
//
// Layout (top → bottom):
//   1. Header row: name, signal chip, active-goal / incident counts.
//   2. Highlights: 2–3 short bullets from analytics.highlights.
//   3. Goals section: one card per goal with trend, latest %, velocity,
//      staleness, and projected mastery date if available.
//   4. Behavior section: incident rollups (this week / last week /
//      by day / by antecedent / likely function).
//   5. AI panel: three AiNarrativeBlocks (analyze, interventions, email).
//
// Nothing here is mocked — every number comes from the caller-provided
// `insight` object.
// ---------------------------------------------------------------------------

const FUNCTION_META = Object.fromEntries(FUNCTIONS_OF_BEHAVIOR.map((f) => [f.value, f]));

function trendMeta(trend) {
  switch (trend) {
    case 'improving':
      return { icon: 'TrendingUp', color: 'text-emerald-600', label: 'Improving' };
    case 'declining':
      return { icon: 'TrendingDown', color: 'text-amber-600', label: 'Declining' };
    case 'regressing':
      return { icon: 'AlertOctagon', color: 'text-red-600', label: 'Regressing' };
    case 'mastered':
      return { icon: 'CheckCircle2', color: 'text-emerald-700', label: 'Mastered' };
    case 'flat':
    default:
      return { icon: 'Minus', color: 'text-slate-500', label: 'Flat' };
  }
}

function GoalCard({ goal }) {
  const t = trendMeta(goal.trend);
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon name={t.icon} size={16} className={t.color} />
          <span className="font-medium text-sm text-slate-900 capitalize">
            {goal.area || 'other'}
          </span>
        </div>
        <span className={`text-xs font-medium ${t.color}`}>{t.label}</span>
      </div>
      {goal.specific && (
        <p className="text-xs text-slate-600 mb-2 line-clamp-2">{goal.specific}</p>
      )}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-slate-500">Latest</div>
          <div className="font-semibold text-slate-900">
            {goal.latest == null ? '—' : `${Math.round(goal.latest)}%`}
          </div>
        </div>
        <div>
          <div className="text-slate-500">Velocity</div>
          <div className="font-semibold text-slate-900">
            {goal.slopePerWeek == null
              ? '—'
              : `${goal.slopePerWeek >= 0 ? '+' : ''}${goal.slopePerWeek.toFixed(1)} pts/wk`}
          </div>
        </div>
        <div>
          <div className="text-slate-500">Last entry</div>
          <div className="font-semibold text-slate-900">
            {goal.staleDays == null
              ? 'no data yet'
              : goal.staleDays === 0
                ? 'today'
                : `${goal.staleDays}d ago`}
          </div>
        </div>
        <div>
          <div className="text-slate-500">Projected mastery</div>
          <div className="font-semibold text-slate-900">
            {goal.projectionDate
              ? new Date(goal.projectionDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
              : goal.isMastered
                ? 'Mastered'
                : goal.isRegressing
                  ? 'not on track'
                  : '—'}
          </div>
        </div>
      </div>
      {goal.entryCount > 0 && (
        <div className="mt-2 text-[10px] text-slate-400">
          Based on {goal.entryCount} data point{goal.entryCount === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
}

function BehaviorSection({ behavior }) {
  if (!behavior || behavior.total === 0) {
    return (
      <div className="border border-slate-200 rounded-lg p-4 text-sm text-slate-500 text-center">
        No behavior incidents logged yet.
      </div>
    );
  }

  const fn = behavior.likelyFunction;
  const fnMeta = fn?.value && fn.value !== 'unknown' ? FUNCTION_META[fn.value] : null;

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Total</div>
          <div className="text-lg font-semibold text-slate-900">{behavior.total}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">This week</div>
          <div className="text-lg font-semibold text-slate-900">{behavior.thisWeek}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Avg intensity</div>
          <div className="text-lg font-semibold text-slate-900">
            {behavior.avgIntensity == null ? '—' : behavior.avgIntensity.toFixed(1)}
          </div>
        </div>
      </div>

      {behavior.patternHeadline && (
        <div className="rounded bg-indigo-50 border border-indigo-100 p-3 text-sm text-indigo-900">
          <div className="flex items-start gap-2">
            <Icon name="Sparkles" size={14} className="text-indigo-600 flex-shrink-0 mt-0.5" />
            <span>{behavior.patternHeadline}</span>
          </div>
        </div>
      )}

      {fnMeta && fn.confidence !== 'low' && (
        <div
          className="rounded p-3 text-sm border"
          style={{ borderColor: `${fnMeta.color}33`, background: `${fnMeta.color}0F`, color: fnMeta.color }}
        >
          <div className="flex items-start gap-2">
            <Icon name={fnMeta.icon} size={14} className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">
                {fn.source === 'stated' ? 'Function: ' : 'Likely function: '}
                {fnMeta.label}
              </div>
              <div className="text-xs opacity-80 mt-0.5">
                {fnMeta.description} · {fn.confidence} confidence, {fn.sampleSize} incident{fn.sampleSize === 1 ? '' : 's'}
                {fn.source === 'inferred' ? ' (inferred from antecedents)' : ' (as stated on the incident form)'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {behavior.topAntecedent && (
          <MiniList title="Top antecedents" items={behavior.byAntecedent.slice(0, 4)} />
        )}
        {behavior.topLocation && (
          <MiniList title="Top locations" items={behavior.byLocation.slice(0, 4)} />
        )}
        {behavior.topTimeBucket && (
          <MiniList title="Time of day" items={behavior.byTimeBucket.slice(0, 4)} />
        )}
        {behavior.byBehaviorType?.length > 0 && (
          <MiniList title="Behavior types" items={behavior.byBehaviorType.slice(0, 4)} />
        )}
      </div>
    </div>
  );
}

function MiniList({ title, items }) {
  const total = items.reduce((n, it) => n + (it.count || 0), 0) || 1;
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mb-1.5">{title}</div>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.value} className="flex items-center gap-2 text-xs">
            <span className="flex-1 truncate text-slate-700">{it.label}</span>
            <div className="flex-1 max-w-[80px] h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-slate-400"
                style={{ width: `${(it.count / total) * 100}%` }}
              />
            </div>
            <span className="text-slate-600 font-medium w-6 text-right">{it.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const StudentInsightPanel = ({ insight, aiEnabled = true, onClose }) => {
  const navigate = useNavigate();

  if (!insight) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
        <Icon name="MousePointer2" size={20} className="text-slate-400 mx-auto mb-2" />
        <p className="text-sm text-slate-500">
          Select a student on the left to see the full breakdown.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900 text-lg">
              {insight.studentName}
            </h3>
            <span className="text-xs text-slate-500">
              {insight.grade ? `${insight.grade} · ` : ''}
              {insight.primaryDisability || 'no primary disability set'}
            </span>
          </div>
          {insight.review?.hasDate && (
            <div className="text-xs text-slate-500 mt-0.5">
              IEP review{' '}
              {insight.review.overdue
                ? `${Math.abs(insight.review.daysLeft)} days overdue`
                : insight.review.dueSoon
                  ? `due in ${insight.review.daysLeft} days`
                  : `on ${insight.review.dueDate}`}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/student-profile-management', { state: { studentId: insight.studentId } })}
          iconName="ExternalLink"
          iconPosition="right"
        >
          Open profile
        </Button>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close panel">
            <Icon name="X" size={16} />
          </Button>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* Highlights */}
        {insight.highlights?.length > 0 && (
          <ul className="space-y-1.5">
            {insight.highlights.map((h, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                <Icon name="Dot" size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Goals */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Target" size={16} className="text-slate-500" />
            <h4 className="font-semibold text-sm text-slate-900">
              IEP goals ({insight.goals?.length || 0})
            </h4>
          </div>
          {insight.goals && insight.goals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {insight.goals.map((g) => (
                <GoalCard key={g.goalId} goal={g} />
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">No goals on file yet.</div>
          )}
        </section>

        {/* Behavior */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertTriangle" size={16} className="text-slate-500" />
            <h4 className="font-semibold text-sm text-slate-900">
              Behavior ({insight.incidentCount} incident{insight.incidentCount === 1 ? '' : 's'})
            </h4>
          </div>
          <BehaviorSection behavior={insight.behavior} />
        </section>

        {/* AI narratives (only when the LLM path is reachable — Supabase is configured) */}
        {aiEnabled && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Sparkles" size={16} className="text-indigo-600" />
              <h4 className="font-semibold text-sm text-slate-900">
                Ask AI to write it up
              </h4>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Every button below sends only aggregate numbers and initials
              ({insight.studentName ? insight.studentName.split(/\s+/).slice(0, 2).map((p) => (p[0] || '').toUpperCase() + '.').join('') : 'M.J.'})
              — never a full name, medical notes, or free-text incident details.
            </p>
            <div className="space-y-3">
              <AiNarrativeBlock
                kind="analyze_student"
                label="Plain-English summary"
                hint="Turn the numbers above into a paragraph you can drop into progress reporting."
                iconName="FileText"
                fetcher={() => analyzeStudentNarrative(insight)}
              />
              {insight.incidentCount > 0 && (
                <AiNarrativeBlock
                  kind="suggest_interventions"
                  label="Suggested interventions"
                  hint="Evidence-based strategies matched to the function of behavior + antecedent pattern."
                  iconName="Lightbulb"
                  fetcher={() => suggestInterventionsNarrative(insight)}
                />
              )}
              <AiNarrativeBlock
                kind="parent_email"
                label="Draft an email to caregiver"
                hint="Warm, honest, 2–3 paragraphs. Copy, paste, personalize."
                iconName="Mail"
                fetcher={() => draftParentEmail(insight, '')}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default StudentInsightPanel;
